import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import makeStyles from "@mui/styles/makeStyles";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Toolbar,
    Typography,
    IconButton,
    Tooltip
} from "@mui/material";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";

import { VisuallyHiddenParagraph } from "./styled/VisuallyHiddenParagraph";
import { getComparator, stableSort } from "./helpers/EditableTableHelpers";

const useStyles = makeStyles(theme => ({
    toolbarContainer: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },
    cell: {
        background: props =>
            props.blurred ? theme.palette?.surface?.disabled ?? "rgba(0, 0, 0, 0.01)" : "none",
        color: props =>
            props.blurred
                ? theme.palette?.text?.disabled ?? "#9e9e9e"
                : theme.palette?.text?.primary ?? "#666666",
        transition: "all 0.1s linear"
    },
    actionCell: { width: "130px", padding: "10px" },
    row: {
        verticalAlign: props => (props.editing ? "top" : "middle")
    }
}));

const TopToolbar = ({ title, addNewRow }) => {
    const classes = useStyles();

    const Title = () => {
        if (typeof title === "string") {
            return (
                <Typography variant="h6" id="tableTitle">
                    {title}
                </Typography>
            );
        }

        return title;
    };

    return (
        <Toolbar className={classes.toolbarContainer}>
            <Title />
            {addNewRow && (
                <Tooltip title="Add new row">
                    <IconButton aria-label="add new row" onClick={addNewRow}>
                        <AddCircleRoundedIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Toolbar>
    );
};

function Head({ order, orderBy, onRequestSort, columns, isSortable, isEditable }) {
    return (
        <TableHead data-testid="table-head">
            <TableRow>
                {columns.map(headCell => {
                    const isCurrentlySorted = orderBy === headCell.field;

                    return (
                        <HeadCell
                            key={`${headCell.field}-${headCell.key ?? ""}`}
                            width={headCell.width}
                            size="medium"
                            align="left"
                            sortDirection={isCurrentlySorted ? order : false}
                        >
                            {isSortable ? (
                                <TableSortLabel
                                    active={isCurrentlySorted}
                                    direction={isCurrentlySorted ? order : "asc"}
                                    onClick={() => onRequestSort(headCell.field)}
                                    data-testid="sort-label"
                                >
                                    <Typography variant="subtitle1">{headCell.title}</Typography>
                                    {isCurrentlySorted && (
                                        <VisuallyHiddenParagraph>
                                            {order === "desc"
                                                ? "sorted descending"
                                                : "sorted ascending"}
                                        </VisuallyHiddenParagraph>
                                    )}
                                </TableSortLabel>
                            ) : (
                                <Typography variant="subtitle1">{headCell.title}</Typography>
                            )}
                        </HeadCell>
                    );
                })}
                {isEditable && (
                    <TableCell align="left">
                        <Typography variant="subtitle1">Actions</Typography>
                    </TableCell>
                )}
            </TableRow>
        </TableHead>
    );
}

const HeadCell = styled(TableCell)`
    width: ${props => (props.width ? props.width : "auto")};
`;

function getFieldNames(columns, row) {
    const names = {};
    columns.forEach(col => {
        names[col.field] = row[col.field] || col.defaultValue || "";
        names.index = row.index;
    });

    return names;
}

function Actions({
    leftAction,
    leftLabel = "Confirm",
    LeftIcon = CheckRoundedIcon,
    rightAction,
    rightLabel = "Cancel",
    RightIcon = CloseRoundedIcon,
    row,
    blurred,
    modifyActionsHelperText
}) {
    const classes = useStyles({ blurred });

    return (
        <TableCell
            align="left"
            className={`${classes.cell} ${classes.actionCell}`}
            data-blurred={blurred}
            data-testid="actions-cell"
        >
            {leftAction && (
                <Tooltip title={leftLabel}>
                    <IconButton
                        aria-label={leftLabel}
                        onClick={() => leftAction(row)}
                        color={blurred ? "inherit" : "default"}
                        size="large"
                    >
                        <LeftIcon />
                    </IconButton>
                </Tooltip>
            )}

            {rightAction && (
                <Tooltip title={rightLabel}>
                    <IconButton
                        aria-label={rightLabel}
                        onClick={() => rightAction(row)}
                        color={blurred ? "inherit" : "default"}
                        size="large"
                    >
                        <RightIcon />
                    </IconButton>
                </Tooltip>
            )}
            {modifyActionsHelperText}
        </TableCell>
    );
}

const rowStates = {
    DEFAULT: "DEFAULT",
    EDITING: "EDITING",
    ADDING: "ADDING",
    DELETING: "DELETING"
};

function Row({
    columns,
    addRow,
    editRow,
    deleteRow,
    rowToDelete,
    setRowToDelete,
    rowToEdit,
    setRowToEdit,
    closeInteractions,
    row = {},
    isAdding,
    validationErrors,
    newRow,
    modifyActionsHelperText
}) {
    const [componentState, setComponentState] = useState(rowStates.DEFAULT);
    const [cellValues, setCellValues] = useState(getFieldNames(columns, row));

    const blurred =
        (isAdding && !newRow) ||
        (!isAdding && rowToDelete !== null && rowToDelete !== row.index) ||
        (!isAdding && rowToEdit !== null && rowToEdit !== row.index);

    const editing = componentState === rowStates.EDITING || componentState === rowStates.ADDING;

    const classes = useStyles({ blurred, editing });

    const handleCellValueChange = (event, field, shouldFieldUpdate = () => true) => {
        event?.persist?.();
        const value = event?.target?.value ?? event;

        if (shouldFieldUpdate(value)) {
            setCellValues(c => ({ ...c, [field]: value }));
        }
    };

    useEffect(() => {
        if (isAdding && newRow && !row.description) {
            setComponentState(rowStates.ADDING);
        } else if (rowToDelete === row.index) {
            setComponentState(rowStates.DELETING);
        } else if (rowToEdit === row.index) {
            setComponentState(rowStates.EDITING);
        } else {
            setComponentState(rowStates.DEFAULT);
        }
    }, [rowToDelete, rowToEdit, isAdding, row, newRow]);

    useEffect(() => {
        if (Object.keys(row).length > 0) {
            setCellValues(getFieldNames(columns, row));
        }
    }, [setCellValues, columns, row]);

    function getDefaultDisplayValue(col, row) {
        if (row[col.field] === undefined || row[col.field] === null) {
            return col.placeholder || "";
        }

        return col.formatting ? col.formatting(row[col.field]) : row[col.field];
    }

    return (
        <TableRow
            hover={!blurred}
            className={classes.row}
            data-editing={editing}
            data-blurred={blurred}
            data-testid="table-row"
        >
            {componentState === rowStates.DEFAULT && (
                <>
                    {columns.map(col => (
                        <TableCell
                            key={`${col.field}-default-${col.key ?? ""}`}
                            align="left"
                            data-blurred={blurred}
                            data-testid="default-cell"
                            className={classes.cell}
                        >
                            {getDefaultDisplayValue(col, row)}
                        </TableCell>
                    ))}

                    {(editRow || deleteRow) && (
                        <Actions
                            leftAction={editRow ? () => setRowToEdit(row.index) : null}
                            leftLabel="Edit"
                            LeftIcon={CreateRoundedIcon}
                            rightAction={deleteRow ? () => setRowToDelete(row.index) : null}
                            rightLabel="Delete"
                            RightIcon={DeleteRounded}
                            row={row}
                            blurred={blurred}
                        />
                    )}
                </>
            )}

            {componentState === rowStates.DELETING && (
                <>
                    <TableCell colSpan={columns.length}>
                        <Typography variant="subtitle1">
                            Are you sure you want to delete this row?
                        </Typography>
                    </TableCell>

                    <Actions
                        leftAction={() => deleteRow(row)}
                        leftLabel="Confirm delete"
                        rightAction={closeInteractions}
                        row={row}
                        modifyActionsHelperText={modifyActionsHelperText}
                    />
                </>
            )}

            {componentState === rowStates.EDITING && (
                <>
                    {columns.map(col => {
                        const EditComponent = col.editComponent || DefaultEditField;

                        return col.editable === false ? (
                            <TableCell
                                key={`${col.field}-edit-${col.key ?? ""}`}
                                align="left"
                                className={classes.cell}
                                data-blurred={blurred}
                                data-testid="default-cell"
                            >
                                {getDefaultDisplayValue(col, row)}
                            </TableCell>
                        ) : (
                            <TableCell
                                key={`${col.field}-edit-${col.key ?? ""}`}
                                align="left"
                                className={classes.cell}
                                data-blurred={blurred}
                            >
                                <EditComponent
                                    value={cellValues[col.field]}
                                    onChange={e =>
                                        handleCellValueChange(e, col.field, col.shouldFieldUpdate)
                                    }
                                    margin="none"
                                    size="small"
                                    error={!!validationErrors[col.field]}
                                    helperText={validationErrors[col.field]}
                                    inputProps={{
                                        style: { fontSize: "0.875rem" },
                                        "data-testid": "editable-cell",
                                        "aria-label": `Edit ${col.title}`
                                    }}
                                />
                            </TableCell>
                        );
                    })}

                    <Actions
                        leftAction={() => editRow(cellValues)}
                        leftLabel="Confirm changes"
                        rightAction={closeInteractions}
                        row={row}
                        modifyActionsHelperText={modifyActionsHelperText}
                    />
                </>
            )}

            {componentState === rowStates.ADDING && (
                <>
                    {columns.map(col => {
                        const EditComponent = col.editComponent || DefaultEditField;
                        return col.editable === false ? (
                            <TableCell
                                key={`${col.field}-add-${col.key ?? ""}`}
                                align="left"
                                className={classes.cell}
                                data-blurred={blurred}
                                data-testid="default-cell"
                            >
                                {getDefaultDisplayValue(col, row)}
                            </TableCell>
                        ) : (
                            <TableCell
                                key={`${col.field}-add-${col.key ?? ""}`}
                                align="left"
                                className={classes.cell}
                                data-blurred={blurred}
                            >
                                <EditComponent
                                    value={cellValues[col.field]}
                                    onChange={e =>
                                        handleCellValueChange(e, col.field, col.shouldFieldUpdate)
                                    }
                                    margin="none"
                                    size="small"
                                    error={!!validationErrors[col.field]}
                                    helperText={validationErrors[col.field]}
                                    inputProps={{
                                        style: { fontSize: "0.875rem" },
                                        "data-testid": "editable-cell",
                                        "aria-label": `New ${col.title}`
                                    }}
                                />
                            </TableCell>
                        );
                    })}

                    <Actions
                        leftAction={() => addRow(cellValues)}
                        leftLabel="Confirm new row"
                        rightAction={closeInteractions}
                        row={row}
                        modifyActionsHelperText={modifyActionsHelperText}
                    />
                </>
            )}
        </TableRow>
    );
}

const DefaultEditField = styled(TextField)({
    width: "130px"
});

function getValidation(columns, data) {
    const validationErrors = {};

    columns.forEach(col => {
        if (col.validation && col.validation.callback) {
            const isCorrect = col.validation.callback(data);

            if (!isCorrect) {
                validationErrors[col.field] = col.validation.errorMessage || "Error";
            }
        }
    });

    return [Object.keys(validationErrors).length === 0, validationErrors];
}

export function EditableMaterialTable({
    title,
    options = {},
    actions = {},
    columns,
    data = [],
    ContainerComponent = DefaultContainer,
    onModifyStateChanged = () => {},
    modifyActionsHelperText
}) {
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState(columns[0]?.field ?? "");
    const [rowToDelete, setRowToDelete] = useState(null);
    const [rowToEdit, setRowToEdit] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        onModifyStateChanged(rowToEdit !== null || rowToDelete !== null || isAdding);
    }, [rowToDelete, rowToEdit, isAdding, onModifyStateChanged]);

    const { addRow, editRow, deleteRow } = actions;
    const { isSortable = true, isEditable = true } = options;

    const handleRequestSort = property => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleAddNewRow = newRowData => {
        const [isValidated, errors] = getValidation(columns, newRowData);

        setValidationErrors(errors);

        if (isValidated) {
            addRow(newRowData);
            closeInteractions();
        }
    };

    const handleEditRow = editedRowData => {
        const [isValidated, errors] = getValidation(columns, editedRowData);

        setValidationErrors(errors);

        if (isValidated) {
            editRow(editedRowData);
            closeInteractions();
        }
    };

    const handleDeleteRow = rowData => {
        deleteRow(rowData);
        closeInteractions();
    };

    const handleSetToDelete = row => {
        closeInteractions();
        setRowToDelete(row);
    };

    const handleSetRowToEdit = row => {
        closeInteractions();
        setRowToEdit(row);
    };

    const handleSetAdding = () => {
        closeInteractions();
        setIsAdding(true);
    };

    const closeInteractions = () => {
        setRowToDelete(null);
        setRowToEdit(null);
        setIsAdding(false);
        setValidationErrors({});
    };

    return (
        <ContainerComponent>
            <TopToolbar
                title={title}
                addNewRow={addRow && isEditable ? handleSetAdding : null}
                closeInteractions={closeInteractions}
            />
            <TableContainer>
                <StyledTable aria-labelledby="tableTitle">
                    <Head
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        columns={columns}
                        isSortable={isSortable}
                        isEditable={Boolean(isEditable && (editRow || deleteRow))}
                    />
                    <TableBody data-testid="table-body">
                        {stableSort(data, getComparator(order, orderBy)).map(row => (
                            <Row
                                key={row.key}
                                row={row}
                                columns={columns}
                                editRow={editRow && isEditable ? handleEditRow : null}
                                deleteRow={deleteRow && isEditable ? handleDeleteRow : null}
                                rowToEdit={rowToEdit}
                                rowToDelete={rowToDelete}
                                setRowToEdit={handleSetRowToEdit}
                                setRowToDelete={handleSetToDelete}
                                closeInteractions={closeInteractions}
                                validationErrors={validationErrors}
                                isAdding={isAdding}
                                modifyActionsHelperText={modifyActionsHelperText}
                            />
                        ))}
                        {isAdding && (
                            <Row
                                key="new row"
                                columns={columns}
                                closeInteractions={closeInteractions}
                                isAdding={isAdding}
                                addRow={handleAddNewRow}
                                validationErrors={validationErrors}
                                newRow
                                modifyActionsHelperText={modifyActionsHelperText}
                            />
                        )}
                        {!data.length && (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length + (isEditable ? 1 : 0)}
                                    align="center"
                                >
                                    No records to display
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        </ContainerComponent>
    );
}

const StyledTable = styled(Table)`
    table-layout: fixed;
`;

const DefaultContainer = styled.div`
    width: 100%;
`;

EditableMaterialTable.propTypes = {
    /**
     * Title displayed on top of the table
     */
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    /**
     * isSortable (default true) - will determine if all columns are sortable or not
     * isEditable (default true) - will determine if all rows are editable or not
     */
    options: PropTypes.shape({
        isSortable: PropTypes.bool,
        isEditable: PropTypes.bool
    }),
    /**
     * addRow (no default) - (newRow) => callback(newRow) - if not passed in "add row" icon will not be displayed
     * editRow (no default) - (editedRow) => callback(editedRow) - if not passed in "edit row" icon will not be displayed. If neither editRow or deleteRow callbacks are provided then the entire actions column will be hidden
     * deleteRow (no default) - (deletedRow) => callback(deletedRow) - if not passed in "delete row" icon will not be displayed. If neither editRow or deleteRow callbacks are provided then the entire actions column will be hidden
     */
    actions: PropTypes.shape({
        addRow: PropTypes.func,
        editRow: PropTypes.func,
        deleteRow: PropTypes.func
    }),
    /**
     * {
     *   title - human readable column title
     *   key - optional key to distinguish column (eg if two columns represent the same field)
     *   field - column identifier (eg. if title is "Annual amount" field could be "annualAmount")
     *   validation: {
     *       callback - (entireRow) => callback(entireRow)
     *       errorMessage (default "Error") - message that will show under the edited cell when validation callback detects any errors
     *   }
     *   shouldFieldUpdate = (cellValue) => callback(cellValue) (returns a boolean) - function (likely a regex check) used to determine if a field should update after user typed in a value. Use it to eg. prevent non numeric characters etc.
     *   formatting - (cellValue) => callback(cellValue) - function used to format the display value when cell is not being edited (its default state)
     *   editComponent - (props) => <Component {...props} /> - custom component that will be used when editing a cell in this column. onChange handler is passed in via props.
     *   defaultValue (type matching the input field (string by default)) - value to be used in the editable component if cell.value is null or undefined
     *   placeholder - value to be displayed in cells default state (when not editing) if cell.value is null or undefined
     *   width - optional string to set a custom width on a column. The default is to evenly size columns
     * }
     */
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            key: PropTypes.string,
            field: PropTypes.string.isRequired,
            validation: PropTypes.shape({
                callback: PropTypes.func.isRequired,
                errorMessage: PropTypes.string
            }),
            shouldFieldUpdate: PropTypes.func,
            formatting: PropTypes.func,
            editComponent: PropTypes.func,
            defaultValue: PropTypes.any,
            placeholder: PropTypes.string,
            width: PropTypes.string
        })
    ).isRequired,
    /**
     * {
     *   index - index position in the original array,
     *   key - unique key for this data item
     *   remaining field keys must correspond to the relevant column.field, otherwise they will not be used
     * }
     */
    data: PropTypes.arrayOf(
        PropTypes.shape({
            index: PropTypes.number.isRequired,
            key: PropTypes.string.isRequired
        })
    ),
    /**
     * (props) => <Component {...props} /> - custom component to be used as a top level table container
     */
    ContainerComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    /**
     * Callback function to be called with boolean value when table enters any of the 'pending confirmation' states
     * (i.e. when adding, deleting or editing a row)
     */
    onModifyStateChanged: PropTypes.func,
    /**
     * Optional helper text to display below the confirm/cancel actions shown when adding/deleting/editing a row
     */
    modifyActionsHelperText: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
};

Actions.propTypes = {
    leftAction: PropTypes.func,
    rightAction: PropTypes.func,
    leftLabel: PropTypes.string,
    rightLabel: PropTypes.string,
    LeftIcon: PropTypes.object,
    RightIcon: PropTypes.object,
    row: PropTypes.object,
    blurred: PropTypes.bool,
    modifyActionsHelperText: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
};

TopToolbar.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    handleAddNewRow: PropTypes.func,
    addNewRow: PropTypes.func
};

Head.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    columns: PropTypes.array,
    isSortable: PropTypes.bool,
    isEditable: PropTypes.bool
};

Row.propTypes = {
    row: PropTypes.object,
    columns: PropTypes.array.isRequired,
    addRow: PropTypes.func,
    editRow: PropTypes.func,
    deleteRow: PropTypes.func,
    rowToDelete: PropTypes.number,
    setRowToDelete: PropTypes.func,
    rowToEdit: PropTypes.number,
    setRowToEdit: PropTypes.func,
    closeInteractions: PropTypes.func,
    isAdding: PropTypes.bool,
    validationErrors: PropTypes.object,
    newRow: PropTypes.bool,
    modifyActionsHelperText: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
};
